import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, throwError, switchMap, catchError, BehaviorSubject, filter, take, firstValueFrom } from 'rxjs';
import { LoginUser } from './models/user';
import { LocalstorageService } from './providers/localstorage.service';
import { AuthService } from './providers/auth.service';
import { RefreshTokenResponse } from './models/token-req-res';
import { routingPath } from './models/constants';
import { ProfilePictureService } from './providers/profile-picture.service';
import { Router } from '@angular/router';

const UNPROTECTED_ENDPOINTS: string[] = [
  '/send-otp',
  '/verify-otp',
  '/refresh-token'
];

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {

  if (UNPROTECTED_ENDPOINTS.some(endpoint => req.url.includes(endpoint))) {
    return next(req);
  }

  const lsService = inject(LocalstorageService);
  const authService = inject(AuthService);
  const profilePictureService = inject(ProfilePictureService);
  const router = inject(Router);

  return from(lsService.getLoggedinUserData()).pipe(
    switchMap((user: LoginUser | null) => {
      if (user?.userSecurityToken) {
        const clonedRequest = req.clone({
          setHeaders: { Authorization: `Bearer ${user.userSecurityToken}` }
        });
        return next(clonedRequest);
      }
      return next(req);
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 499 || error.error?.message === 'Please Provide Authentication Token') {
        return handleTokenRefresh(req, next, lsService, authService, profilePictureService, router);
      }
      return throwError(() => error);
    })
  );
};

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function handleTokenRefresh(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  lsService: LocalstorageService,
  authService: AuthService,
  profilePictureService: ProfilePictureService,
  router: Router
  ): Observable<HttpEvent<any>> {

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return from(lsService.getLoggedinUserData()).pipe(
      switchMap(user => {
        if (!user?.userRefreshToken || !user?.mobile) {
          // return throwError(() => new Error("No refresh token available"));
          // Logout user and return an error observable
          return from(logout(lsService, profilePictureService, router)).pipe(
            switchMap(() => throwError(() => new Error("Session expired. Please login again.")))
          );
        }

        const refreshTokenRequest = {
          creatorId: user.userDetails?.creatorId ?? '',
          refreshToken: user.userRefreshToken
        };

        // Convert Promise to Observable using `from()`
        return from(authService.refreshToken(refreshTokenRequest)).pipe(
          switchMap((newTokens: RefreshTokenResponse) => {
            return from(lsService.updateAccessToken(newTokens)).pipe(
              switchMap(() => {
                refreshTokenSubject.next(newTokens.accessToken);
                isRefreshing = false;

                // Retry the failed request with the new token
                const newRequest = req.clone({
                  setHeaders: { Authorization: `Bearer ${newTokens.accessToken}` }
                });
                return next(newRequest);
              })
            );
          }),
          catchError(err => {
            isRefreshing = false;
            refreshTokenSubject.next(null);
            lsService.deleteLoggedinUserData(); // Logout user if refresh fails
            return throwError(() => err);
          })
        );
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const newRequest = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next(newRequest);
      })
    );
  }
}

async function logout(
  lsService: LocalstorageService,
  profilePictureService: ProfilePictureService,
  router: Router) {
  try {
    await lsService.deleteLoggedinUserData();

    // Clear profile picture and wait for completion
    await firstValueFrom(profilePictureService.clearProfilePicture());
    
    const initialUser: LoginUser = {
      userOneSignalID: '', 
      userSecurityToken: '', 
      userDetails: undefined, 
      mobile: undefined,
      userRefreshToken: '',
      userTokenType: ''
    }

    await lsService.setLoggedinUserData(initialUser);
    router.navigate([routingPath.landing], { replaceUrl: true });
  } catch (error) {
    console.error('Logout error:', error);
  }
}
